import React, { useState, useEffect } from "react"
import {
    Typography,
    Container,
    Box,
    AppBar,
    Toolbar,
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    useTheme,
    useMediaQuery,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import { ArrowBack } from "@mui/icons-material"

// Policy section component using Material UI
const PolicySection = ({ title, content }) => {
    return (
        <Card
            sx={{
                backgroundColor: "rgba(15, 23, 42, 0.5)",
                borderColor: "rgba(30, 41, 59, 0.8)",
                borderRadius: 1,
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                transition: "box-shadow 0.2s",
                "&:hover": {
                    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                },
            }}
            variant="outlined"
        >
            <CardHeader
                title={
                    <Typography variant="h6" sx={{ color: "#FFF", fontWeight: 600 }}>
                        {title}
                    </Typography>
                }
                sx={{ pb: 1 }}
            />
            <Divider sx={{ mx: 3, backgroundColor: "rgba(71, 85, 105, 0.8)" }} />
            <CardContent sx={{ pt: 2 }}>
                <Box sx={{ color: "rgba(203, 213, 225, 0.9)" }}>
                    {typeof content === "string" ? (
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                            {content}
                        </Typography>
                    ) : (
                        content
                    )}
                </Box>
            </CardContent>
        </Card>
    )
}

const PrivacyPolicyPage = () => {
    const [currentDate, setCurrentDate] = useState("")
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const navigate = useNavigate()

    useEffect(() => {
        setCurrentDate(new Date().toLocaleDateString())
    }, [])

    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: "none",
                color: "rgba(226, 232, 240, 0.9)",
                display: "flex",
                flexDirection: "column",
                zIndex: 0
            }}
        >
            {/* Sticky Header */}
            <AppBar
                position="sticky"
                sx={{
                    backgroundColor: "rgba(10, 15, 21, 0)",
                    boxShadow: "none",
                    zIndex: 1
                }}
            >
                <Toolbar>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => window.history.back()}
                        sx={{
                            color: "rgba(226, 232, 240, 0.9)",
                            "&:hover": {
                                backgroundColor: "rgba(30, 41, 59, 0.5)",
                            },
                        }}
                    >
                        Back
                    </Button>
                </Toolbar>
            </AppBar>

            {/* Main Content */}
            <Container
                maxWidth="md"
                sx={{
                    pb: { xs: 4, md: 6 },
                    px: { xs: 2, md: 3 },
                    flexGrow: 1,
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {/* Header Section */}
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                color: "#FFF",
                                mb: 1,
                            }}
                        >
                            Privacy Policy
                        </Typography>
                        <Typography variant="body2" sx={{ color: "rgba(148, 163, 184, 0.9)" }}>
                            Last updated: {currentDate}
                        </Typography>
                    </Box>

                    {/* Introduction */}
                    <Card
                        sx={{
                            backgroundColor: "rgba(15, 23, 42, 0.5)",
                            borderColor: "rgba(30, 41, 59, 0.8)",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        }}
                        variant="outlined"
                    >
                        <CardContent>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "rgba(203, 213, 225, 0.9)",
                                    lineHeight: 1.7,
                                }}
                            >
                                At Time Capsule, we take your privacy seriously. This Privacy Policy explains how we collect, use, and
                                protect your personal information when you use our service.
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Policy Sections */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <PolicySection
                            title="Information We Collect"
                            content={
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.7 }}>
                                        We collect information that you provide directly to us, including:
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.5 } }}>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Account information (name, email, password)
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Profile information
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Content you upload (photos, messages)
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Communication preferences
                                        </Typography>
                                    </Box>
                                </Box>
                            }
                        />

                        <PolicySection
                            title="How We Use Your Information"
                            content={
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.7 }}>
                                        We use the information we collect to:
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.5 } }}>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Provide and maintain our service
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Notify you about changes to our service
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Provide customer support
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Monitor usage of our service
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Detect, prevent, and address technical issues
                                        </Typography>
                                    </Box>
                                </Box>
                            }
                        />

                        <PolicySection
                            title="Data Security"
                            content="We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security."
                        />

                        <PolicySection
                            title="Your Rights"
                            content={
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.7 }}>
                                        You have the right to:
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.5 } }}>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Access your personal information
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Correct inaccurate information
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Request deletion of your information
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Object to processing of your information
                                        </Typography>
                                        <Typography component="li" variant="body2" sx={{ lineHeight: 1.7 }}>
                                            Request data portability
                                        </Typography>
                                    </Box>
                                </Box>
                            }
                        />

                        <PolicySection
                            title="Contact Us"
                            content={
                                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                    If you have any questions about this Privacy Policy, please{" "}
                                    <Box
                                        component="span"
                                        onClick={() => navigate("/contact")}
                                        sx={{
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                            "&:hover": {
                                                color: "#FFF",
                                            },
                                        }}
                                    >
                                        contact us here
                                    </Box>
                                    .
                                </Typography>
                            }
                        />
                    </Box>
                </Box>
            </Container>

            {!isMobile &&
                <Box
                    component="footer"
                    sx={{
                        borderTop: "1px solid rgba(30, 41, 59, 0.8)",
                        py: 3,
                        mt: "auto",
                    }}
                >
                    <Container>
                        <Typography variant="body2" align="center" sx={{ color: "rgba(148, 163, 184, 0.9)" }}>
                            © {new Date().getFullYear()} Time Capsule. All rights reserved.
                        </Typography>
                    </Container>
                </Box>
            }
        </Box>
    )
}

export default PrivacyPolicyPage
